import { holdInUtc, PushNotification } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { useCallback } from "react";

import { EnrollmentValidationError } from "../models";
import * as Queries from "./queries";
import { useRepIncidentApi } from "./repIncidentApi";
import { useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useEnrollmentValidationErrorApi() {
	const { Get, Post } = useRepPortalApi();
	const { assignToQaAnalyst } = useRepIncidentApi();

	const initialize = useCallback(function (eve: EnrollmentValidationError) {
		eve.dateOfSale = holdInUtc(eve.dateOfSale);
		//Customer Phone gets formatted by masked component in Investigation, but if Investigation isn't shown
		//(i.e. clicked by user), component never gets opportunity to mask.  So this makes validation work.
		const phone = eve.customerPhoneNumber;
		if (phone && /\d{10}/.test(phone)) {
			eve.customerPhoneNumber = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
		}
		return eve;
	}, []);

	const saveEnrollmentValidationError = useCallback(
		function (eve: EnrollmentValidationError, email: boolean) {
			return Post<number>("SaveEnrollmentValidationError", null, {
				eve,
				email,
			});
		},
		[Post],
	);

	const loadEnrollmentValidationError = useCallback(
		async function (id: number) {
			return Get<EnrollmentValidationError>("GetEnrollmentValidationError", {
				id,
			}).then(initialize);
		},
		[Post, initialize],
	);

	const getEnrollmentValidationErrors = useCallback(
		async function (representativeId: number) {
			const eves = await Get<EnrollmentValidationError[]>("EnrollmentValidationErrors", {
				representativeId,
			});
			return eves.map(initialize);
		},
		[Get, initialize],
	);

	const loadEnrollmentValidationErrors = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultArr();
				try {
					const eves = await getEnrollmentValidationErrors(repId);

					return new KeyedArray<EnrollmentValidationError>(eves, (b) => b.id);
				} catch (e) {
					PushNotification("Unable to retrieve enrollment validation errors");
					return defaultArr();
				}
			};
		},
		[getEnrollmentValidationErrors],
	);

	const createIncident = useCallback(
		async function (id: number) {
			return await Get<number>("CreateCustomerIncident", { id });
		},
		[Get],
	);

	const assignToAnalyst = assignToQaAnalyst("EnrollmentValidations");

	const useEVEsQuery = (repId: number) => {
		return useQueryWrapper(
			Queries.ENROLLMENTVALIDATIONS,
			[repId, loadEnrollmentValidationErrors],
			loadEnrollmentValidationErrors(repId),
		);
	};

	return {
		saveEnrollmentValidationError,
		loadEnrollmentValidationError,
		useEVEsQuery,
		getEnrollmentValidationErrors,
		assignToAnalyst,
		createIncident,
	};
}

const defaultArr = () => new KeyedArray<EnrollmentValidationError>([], (b) => b.id);
